import { type PropertyValues, html, nothing } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { SignalElement, inject } from "ui-common/di";
import { getSocialIcon } from "ui-components/helpers/socialIconMapping";
import "ui-components/skeleton-circle";
import "ui-components/user-theme-button/user-theme-button";
import { getAssetUrl } from "helpers/assetHelpers";

import { styles } from "./posts-mobile-header.css";

import type { ICreatorProfileDataState } from "../../services/creator-profile-data/types";
import { t } from "../../translations/i18n";
import "../user-avatar/user-avatar";
import { parseSocialMediaUrls, type ParsedSocialLink } from "ui-common/socialMediaConfig";
import { isVideoMimeType } from "helpers/fileValidation";

export type TabType = "links" | "myWorld" | "discounts";

@customElement("posts-mobile-header")
export class PostsMobileHeader extends SignalElement {
  static override styles = styles;

  @inject<ICreatorProfileDataState>("creatorProfileDataState")
  private creatorProfileDataState!: ICreatorProfileDataState;

  @property({ type: String })
  primaryColor?: string;

  @property({ type: String })
  secondaryColor?: string;

  @property({ type: String })
  activeTab: TabType = "links";

  @state()
  private tabIndicatorStyle = "";

  private get parsedSocialLinks(): ParsedSocialLink[] {
    const links = this.creatorProfileDataState.profile.get()?.socialLinks;
    if (!links) {
      return [];
    }
    return parseSocialMediaUrls(links);
  }

  private get currentThemeColors(): { primary: string; secondary: string } {
    // Priority 1: Use colors passed as properties (from parent's dynamic state)
    if (this.primaryColor && this.secondaryColor) {
      return {
        primary: this.primaryColor,
        secondary: this.secondaryColor,
      };
    }

    // Priority 2: Fallback to profile data (server state)
    const profileData = this.creatorProfileDataState.profile.get();

    // Priority 2a: Selected theme colors (if a theme is selected)
    if (profileData?.theme) {
      return {
        primary: profileData.theme.primaryColor,
        secondary: profileData.theme.secondaryColor,
      };
    }

    // Priority 2b: Custom colors (if no theme is selected but custom colors exist)
    if (profileData?.customColors) {
      return {
        primary: profileData.customColors.primary,
        secondary: profileData.customColors.secondary,
      };
    }

    // Fallback: Default colors
    return {
      primary: "#FF5733",
      secondary: "#33C1FF",
    };
  }

  private get backgroundImageUrl(): string | undefined {
    const url = this.creatorProfileDataState.profile.get()?.backgroundImageUrl;
    return url || undefined;
  }

  private get isVideoBackground(): boolean {
    const serverMimeType = this.creatorProfileDataState.profile.get()?.backgroundImageMimeType;
    const backgroundUrl = this.backgroundImageUrl;

    if (serverMimeType) {
      return isVideoMimeType(serverMimeType);
    }

    // Fallback to URL detection
    if (backgroundUrl) {
      const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi"];
      const urlLower = backgroundUrl.toLowerCase();
      return videoExtensions.some((ext) => urlLower.includes(ext));
    }

    return false;
  }

  private handleTabClick = (tab: TabType) => {
    if (tab !== this.activeTab) {
      this.activeTab = tab;
      this.updateTabIndicator();

      // Emit tab change event
      this.dispatchEvent(
        new CustomEvent("tab-changed", {
          detail: { activeTab: tab },
          bubbles: true,
        }),
      );
    }
  };

  private updateTabIndicator() {
    // Wait for next frame to ensure DOM is updated
    requestAnimationFrame(() => {
      const tabElements = this.shadowRoot?.querySelectorAll(".tab");
      const activeTabElement = this.shadowRoot?.querySelector(".tab.active");

      if (activeTabElement && tabElements && tabElements.length > 0) {
        const tabRect = activeTabElement.getBoundingClientRect();
        const containerElement = tabElements[0]?.parentElement;

        if (containerElement) {
          const containerRect = containerElement.getBoundingClientRect();
          const left = tabRect.left - containerRect.left;
          const width = tabRect.width;

          this.tabIndicatorStyle = `
            transform: translateX(${left}px);
            width: ${width}px;
          `;
        }
      }
    });
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has("activeTab")) {
      this.updateTabIndicator();
    }
  }

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.updateTabIndicator();
  }

  private renderSocialLinks() {
    const maxVisible = 3;

    // Loading state - show 3 skeleton circles
    if (this.creatorProfileDataState.loading.get()) {
      return html`
        <div class="social-links">
          ${Array.from(
            { length: maxVisible },
            () => html`
              <div class="social-link-button">
                <skeleton-circle elevation="1"></skeleton-circle>
              </div>
            `,
          )}
        </div>
      `;
    }

    if (!this.creatorProfileDataState.loading.get() && !this.creatorProfileDataState.profile.get()) {
      return html`<div class="social-links"></div>`;
    }

    const socialLinks = this.parsedSocialLinks;
    const visibleLinks = socialLinks.slice(0, maxVisible);
    const remainingCount = socialLinks.length - maxVisible;
    const themeColors = this.currentThemeColors;

    return html`
      <div class="social-links">
        ${visibleLinks.map((link) =>
          link.platform !== "other"
            ? html`
                <user-theme-button
                  .circle=${true}
                  .primaryColor=${themeColors.primary}
                  .secondaryColor=${themeColors.secondary}
                  .ariaLabel=${"Show " + remainingCount + " more social links"}
                  .size=${"small"}
                  .interactive=${false}
                >
                  ${getSocialIcon(link.platform)}
                </user-theme-button>
              `
            : nothing,
        )}
        ${remainingCount > 0
          ? html`
              <user-theme-button
                .circle=${true}
                .primaryColor=${themeColors.primary}
                .secondaryColor=${themeColors.secondary}
                .ariaLabel=${"Show " + remainingCount + " more social links"}
                .size=${"small"}
                .interactive=${false}
              >
                +${remainingCount}
              </user-theme-button>
            `
          : ""}
      </div>
    `;
  }

  private renderTabs() {
    const tabs = [
      { key: "links" as TabType, label: t("posts.linksTab") },
      { key: "myWorld" as TabType, label: t("posts.myWorldTab") },
      { key: "discounts" as TabType, label: t("posts.discountsTab") },
    ];

    return html`
      <div class="tab-navigation">
        <div class="tab-container">
          ${tabs.map(
            (tab) => html`
              <button
                class="tab ${this.activeTab === tab.key ? "active" : ""}"
                @click=${() => this.handleTabClick(tab.key)}
              >
                ${tab.label}
              </button>
            `,
          )}
        </div>
        <div class="tab-indicator" style=${this.tabIndicatorStyle}></div>
      </div>
    `;
  }

  protected override render() {
    const hasBackground = Boolean(this.backgroundImageUrl);
    const themeColors = this.currentThemeColors;

    const backgroundStyle =
      hasBackground && !this.isVideoBackground ? `background-image: url(${getAssetUrl(this.backgroundImageUrl)})` : "";

    const themeStyle = `
      --theme-primary-color: ${themeColors.primary};
      --theme-secondary-color: ${themeColors.secondary};
      ${backgroundStyle}
    `;

    return html`
      <div
        class="posts-mobile-header ${hasBackground ? "has-background" : ""} ${this.isVideoBackground ? "is-video" : ""}"
        style=${themeStyle}
      >
        <!-- Video background (when applicable) -->
        ${this.isVideoBackground && hasBackground && this.backgroundImageUrl
          ? (() => {
              const videoSrc = getAssetUrl(this.backgroundImageUrl) || "";
              return html`
                <video
                  class="background-video"
                  loop
                  muted
                  playsinline
                  preload="auto"
                  src=${videoSrc}
                  @loadeddata=${(e: Event) => {
                    const video = e.target as HTMLVideoElement;
                    video.play().catch((err) => console.error("Video play failed:", err));
                  }}
                  @error=${(e: Event) => console.error("Video error:", (e.target as HTMLVideoElement)?.error)}
                >
                  Your browser does not support the video tag.
                </video>
              `;
            })()
          : ""}

        <!-- Gradient overlay -->
        <div class="gradient-overlay"></div>

        <!-- User profile overlay -->
        <div class="user-profile-overlay">
          <user-avatar size="medium"></user-avatar>
          <div class="user-name">
            ${this.creatorProfileDataState.profile.get()?.handle || t("user.defaultGenericUserName")}
          </div>
          ${this.renderSocialLinks()}
        </div>

        <!-- Tab navigation -->
        ${this.renderTabs()}
      </div>
    `;
  }
}
